/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Typography2 } from '@coursera/cds-core';

import type { AutoSendCadence } from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/types';
import ToggleSwitch from 'bundles/enterprise-ui/components/ToggleSwitch';

import _t from 'i18n!nls/enterprise-admin-messaging-v2';

type Props = {
  /** Whether auto-send-to-new-matches is on. */
  enabled: boolean;
  /** Selected cadence; only meaningful (and interactive) when `enabled`. */
  cadence: AutoSendCadence;
  onEnabledChange: (enabled: boolean) => void;
  onCadenceChange: (cadence: AutoSendCadence) => void;
};

const CADENCES: AutoSendCadence[] = ['7_days', '15_days', '30_days'];

const cadenceLabel = (cadence: AutoSendCadence): string => {
  switch (cadence) {
    case '7_days':
      return _t('7 days');
    case '15_days':
      return _t('15 days');
    case '30_days':
      return _t('30 days');
    default:
      return _t('15 days');
  }
};

const styles = {
  row: css`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: var(--cds-spacing-200);
    padding: var(--cds-spacing-200);
    background: var(--cds-color-interactive-background-primary-hover-weak);
    border-radius: var(--cds-spacing-100);
  `,
  copy: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-50);
  `,
  supportText: css`
    color: var(--cds-color-neutral-primary-weak);
  `,
  cadenceGroup: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-100);
    margin-top: var(--cds-spacing-150);
  `,
  cadencePills: css`
    display: flex;
    flex-wrap: wrap;
    gap: var(--cds-spacing-100);
  `,
  /* Pill-shaped cadence toggle. Neutral light-grey border on every pill;
     selected state fills with `interactive/secondarySelected` (dark grey)
     and reverses the text color to white. Disabled (auto-send off) dims the
     whole group per Figma 1031-24885. */
  cadencePill: css`
    display: inline-flex;
    align-items: center;
    height: 36px;
    padding: 0 var(--cds-spacing-200);
    background: var(--cds-color-neutral-background-primary);
    border: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    border-radius: 9999px;
    color: var(--cds-color-neutral-primary);
    cursor: pointer;
    font-family: inherit;
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    white-space: nowrap;

    &:hover:not(:disabled) {
      background: var(--cds-color-neutral-disabled-weak);
    }

    &:focus-visible {
      outline: 2px solid var(--cds-color-interactive-stroke-primary-focus);
      outline-offset: 2px;
    }

    &:disabled {
      cursor: default;
      opacity: 0.5;
    }
  `,
  cadencePillSelected: css`
    background: var(--cds-color-interactive-secondary-selected);
    color: var(--cds-color-neutral-background-primary);

    &:hover:not(:disabled) {
      background: var(--cds-color-interactive-secondary-hover);
    }
  `,
  cadenceLabelDisabled: css`
    color: var(--cds-color-neutral-disabled-strong);
  `,
};

/**
 * "Auto-send to new matches" card. Lets the admin opt in to automatically
 * sending the same message to learners who match this audience in the future,
 * on a selectable cadence. Shared between the Message-sent confirmation screen
 * (AI flow) and the Edit-and-resend modal.
 */
const AutoSendToNewMatches = ({ enabled, cadence, onEnabledChange, onCadenceChange }: Props) => {
  // Using useID as there can be multiple instances of this component on the same page.
  const cadenceLabelId = React.useId();

  return (
    <div css={styles.row}>
      <div css={styles.copy}>
        <Typography2 component="p" variant="subtitleMedium">
          {_t('Auto-send to new matches')}
        </Typography2>
        <Typography2 component="p" variant="bodySecondary" css={styles.supportText}>
          {_t('New learners matching this audience will automatically receive this message.')}
        </Typography2>
        <div css={styles.cadenceGroup}>
          {/* Row 17: "Repeat every" renders at 16px (subtitleMedium) per design. */}
          <Typography2
            id={cadenceLabelId}
            component="p"
            variant="subtitleMedium"
            css={!enabled && styles.cadenceLabelDisabled}
          >
            {_t('Repeat every')}
          </Typography2>
          <div
            role="group"
            aria-labelledby={cadenceLabelId}
            css={styles.cadencePills}
            data-testid="auto-send-cadence-row"
          >
            {CADENCES.map((option) => {
              const selected = cadence === option;
              return (
                <button
                  key={option}
                  type="button"
                  css={[styles.cadencePill, selected && enabled && styles.cadencePillSelected]}
                  onClick={() => onCadenceChange(option)}
                  disabled={!enabled}
                  aria-pressed={selected}
                  data-testid={`auto-send-cadence-${option}`}
                >
                  {cadenceLabel(option)}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      <ToggleSwitch
        aria-label={_t('Auto-send to new matches')}
        data-testid="auto-send-toggle"
        checked={enabled}
        onChange={onEnabledChange}
      />
    </div>
  );
};

export default AutoSendToNewMatches;
